import { useState } from 'react';

// Blueprint palette for the schematic fallbacks.
const LINE = '#e5e7eb';
const RED = '#e53e44';
const WATER = 'rgba(148, 163, 184, 0.35)';
const WATER_LINE = '#94a3b8';
const FILL = 'rgba(255, 255, 255, 0.04)';
const S = { stroke: LINE, strokeWidth: 2, fill: FILL };

function Blueprint({ children, code }) {
  return (
    <svg viewBox="0 0 400 300" className="h-full w-full" role="img" aria-label={`${code} schematic`}>
      <defs>
        <pattern id={`grid-${code}`} width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="400" height="300" fill="#1d2126" />
      <rect width="400" height="300" fill={`url(#grid-${code})`} />
      <line x1="0" y1="250" x2="400" y2="250" stroke={LINE} strokeWidth="1" strokeDasharray="4 4" opacity="0.4" />
      <text x="16" y="286" fontSize="10" fill="#6b7280" fontFamily="monospace" letterSpacing="1">
        SCHEMATIC · {code} · NOT TO SCALE
      </text>
      {children}
    </svg>
  );
}

const Wave = ({ x, y, w }) => (
  <path
    d={`M${x} ${y} q${w / 8} -6 ${w / 4} 0 t${w / 4} 0 t${w / 4} 0 t${w / 4} 0`}
    fill="none"
    stroke={WATER_LINE}
    strokeWidth="2"
  />
);

const Label = ({ x, y, children }) => (
  <text x={x} y={y} textAnchor="middle" fontSize="10" fontWeight="700" fill={LINE} fontFamily="monospace" letterSpacing="1">
    {children}
  </text>
);

const scenes = {
  wtp: (
    <>
      {[70, 175, 280].map((cx, i) => (
        <g key={cx}>
          <rect x={cx - 42} y="170" width="84" height="80" {...S} />
          <ellipse cx={cx} cy="170" rx="42" ry="14" fill={WATER} stroke={LINE} strokeWidth="2" />
          <Label x={cx} y={225}>{['COAG.', 'FLOC.', 'DISINF.'][i]}</Label>
        </g>
      ))}
      <path d="M112 200 H133 M217 200 H238 M322 200 H360 V120" stroke={RED} strokeWidth="4" fill="none" />
      <path d="M360 92 c-10 14 -14 22 -14 30 a14 14 0 0 0 28 0 c0 -8 -4 -16 -14 -30z" fill={RED} />
    </>
  ),
  stp: (
    <>
      <rect x="40" y="140" width="250" height="110" {...S} />
      <rect x="42" y="160" width="246" height="88" fill={WATER} />
      <Wave x={42} y={160} w={246} />
      {Array.from({ length: 20 }).map((_, i) => (
        <circle key={i} cx={60 + ((i * 37) % 220)} cy={180 + ((i * 23) % 60)} r={2 + (i % 3)} fill="none" stroke={LINE} />
      ))}
      <rect x="310" y="190" width="60" height="60" rx="6" {...S} />
      <path d="M310 240 H290" stroke={RED} strokeWidth="4" />
      <Label x={165} y={130}>MBBR · MBR · SBR</Label>
    </>
  ),
  etp: (
    <>
      <path d="M20 250 V170 l35 -20 v20 l35 -20 v20 l35 -20 V250z" {...S} />
      <rect x="95" y="100" width="16" height="70" {...S} />
      <path d="M125 230 H165" stroke={RED} strokeWidth="4" />
      {[205, 300].map((x, i) => (
        <g key={x}>
          <rect x={x - 40} y="150" width="80" height="100" {...S} />
          <rect x={x - 38} y="200" width="76" height="48" fill={WATER} />
          <Label x={x} y={180}>{i ? 'UF + RO' : 'O&G TRAP'}</Label>
        </g>
      ))}
      <path d="M245 215 H260 M340 215 H380 V250" stroke={RED} strokeWidth="4" fill="none" />
    </>
  ),
  gwt: (
    <>
      {/* house */}
      <path d="M40 150 l70 -55 l70 55 V250 H40z" {...S} />
      <rect x="95" y="195" width="30" height="55" {...S} />
      <rect x="58" y="165" width="28" height="22" fill={WATER} stroke={LINE} strokeWidth="1.5" />
      <rect x="134" y="165" width="28" height="22" fill={WATER} stroke={LINE} strokeWidth="1.5" />
      <Label x={110} y={142}>BATH · BASIN</Label>
      {/* grey water out */}
      <path d="M180 235 H255" stroke={WATER_LINE} strokeWidth="4" strokeDasharray="8 6" />
      {/* treatment tank */}
      <rect x="255" y="175" width="100" height="75" rx="6" {...S} />
      <rect x="257" y="205" width="96" height="43" rx="4" fill={WATER} />
      <Wave x={257} y={205} w={96} />
      <Label x={305} y={195}>GWT UNIT</Label>
      {/* reuse loop */}
      <path d="M305 170 C305 100 220 70 168 86" fill="none" stroke={RED} strokeWidth="4" />
      <path d="M176 74 L156 90 L180 98z" fill={RED} />
      <Label x={250} y={74}>REUSE</Label>
      {/* garden */}
      <path d="M372 250 c0 -30 8 -44 20 -54 c-2 20 -8 34 -20 54z" fill="none" stroke={LINE} strokeWidth="2" />
    </>
  ),
  zld: (
    <>
      {[70, 140, 210].map((x, i) => (
        <g key={x}>
          <rect x={x - 22} y={90 + i * 10} width="44" height={160 - i * 10} rx="20" {...S} />
          <rect x={x - 20} y="200" width="40" height="48" rx="6" fill={WATER} />
          <path d={`M${x - 6} ${82 + i * 10} q-6 -12 0 -24 q6 -12 0 -24`} stroke={WATER_LINE} strokeWidth="2" fill="none" />
          <Label x={x} y={145 + i * 10}>{`E${i + 1}`}</Label>
        </g>
      ))}
      <path d="M92 220 H118 M162 220 H188 M232 220 H270 V190 H300" stroke={RED} strokeWidth="4" fill="none" />
      <path d="M300 170 h70 l-20 40 h-30z" {...S} />
      {[[300, 240], [320, 232], [342, 242], [362, 236], [331, 250]].map(([x, y]) => (
        <path key={`${x}-${y}`} d={`M${x} ${y} l8 -10 l8 10 l-8 10z`} fill={RED} opacity="0.85" />
      ))}
      <Label x={335} y={160}>SOLIDS</Label>
      <Label x={140} y={60}>MULTI-EFFECT EVAPORATOR</Label>
    </>
  ),
};

export default function ProductVisual({ product, className = '' }) {
  const [imageFailed, setImageFailed] = useState(!product.image);

  return (
    <div className={`overflow-hidden ${className}`}>
      {imageFailed ? (
        <Blueprint code={product.code}>{scenes[product.id]}</Blueprint>
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}${product.image}`}
          alt={product.name}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      )}
    </div>
  );
}
